import React, { useState } from 'react';
import { Play, Star, CheckCircle, Trash2, Loader2 } from 'lucide-react';

/**
 * TrackCard - Displays a single track with playback and action buttons
 * Used in Upcoming, Spotlight, Finished, and Removed tabs
 */
const TrackCard = ({ 
  track, 
  isReviewer, 
  onPlay, 
  onSpotlight, 
  onFinish, 
  onRemove, 
  showActions = true, 
  showFinishButton = true, 
  isSpotlighted = false,
  isRemoved = false,
  spotlightOnly = false
}) => {
  const [isLoading, setIsLoading] = useState(false);
  
  // Debug handler with visual feedback - VERSION 2.1
  const handlePlayClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Immediate visual feedback
    setIsLoading(true);
    
    // VERSION CHECK - this log confirms new code is loaded
    console.log('🎵 PLAY CLICKED [v2.1]:', track?.title || track?.track_title);
    
    if (onPlay) {
      try {
        onPlay();
      } catch (err) {
        console.error('🎵 Play error:', err);
        alert('Play error: ' + err.message);
      }
    } else {
      console.error('🎵 No onPlay handler!');
      alert('Error: No play handler attached to this button');
    }
    
    // Reset loading state after a delay
    setTimeout(() => setIsLoading(false), 2000);
  };

  const getPlatformIcon = (platform) => {
    switch(platform) {
      case 'spotify': return '🎵 Spotify';
      case 'soundcloud': return '🔊 SoundCloud';
      case 'youtube': return '📺 YouTube';
      case 'file': return '🎧 File Upload';
      default: return '🎵 Music';
    }
  };

  // Get submission type badge color and text
  const getSubmissionBadge = (submissionType) => {
    switch(submissionType) {
      case 'custom':
        return { color: 'bg-green-500', text: '💚 CUSTOM', textColor: 'text-white' };
      case 'superskip':
        return { color: 'bg-red-500', text: '🔴 SUPERSKIP', textColor: 'text-white' };
      case 'skip':
        return { color: 'bg-yellow-500', text: '🟡 SKIP', textColor: 'text-black' };
      default:
        return null;
    }
  };

  const submissionBadge = getSubmissionBadge(track.submission_type);

  // Check if track is spotlighted (from track data or prop)
  const trackIsSpotlighted = track.is_spotlighted || isSpotlighted;

  return (
    <div className={`bg-[#111] border rounded-lg p-3 sm:p-4 lg:p-6 transition relative w-full max-w-full box-border ${
      isRemoved 
        ? 'border-red-500/50 opacity-60' 
        : 'border-[#F2D857]/20 hover:border-[#F2D857]/50'
    }`}>
      {/* Removed Badge - Top Left (takes priority over submission badge) */}
      {isRemoved && (
        <div className="absolute top-2 left-2 z-10 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
          ❌ REMOVED
        </div>
      )}
      
      {/* Skip/Superskip/Custom Badge - Top Left (only show if not removed) */}
      {!isRemoved && submissionBadge && (
        <div className={`absolute top-2 left-2 z-10 ${submissionBadge.color} ${submissionBadge.textColor} px-2 py-1 rounded-full text-xs font-bold`}>
          {submissionBadge.text}
        </div>
      )}
      
      {/* Spotlight Badge - Top Right */}
      {trackIsSpotlighted && (
        <div className="absolute top-2 right-2 z-10">
          <Star className="w-4 h-4 sm:w-5 sm:h-5 text-[#F2D857] fill-[#F2D857]" />
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4 flex-1 w-full min-w-0">
          {/* Artwork with play overlay */}
          <div className="relative flex-shrink-0">
            {track.artwork_url || track.cover_art ? (
              <>
                <img 
                  src={track.artwork_url || track.cover_art} 
                  alt={track.title}
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded object-cover"
                />
                {onPlay && (
                  <button
                    onClick={handlePlayClick}
                    onTouchEnd={handlePlayClick}
                    className={`absolute inset-0 rounded flex items-center justify-center transition group ${
                      isLoading ? 'bg-green-500/70' : 'bg-black/50 hover:bg-black/70'
                    }`}
                    data-testid="track-play-button"
                    style={{ touchAction: 'manipulation' }}
                  >
                    {isLoading ? (
                      <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 text-white animate-spin" />
                    ) : (
                      <Play className="w-6 h-6 sm:w-8 sm:h-8 text-[#F2D857] group-hover:scale-110 transition" />
                    )}
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={handlePlayClick}
                onTouchEnd={handlePlayClick}
                className={`w-14 h-14 sm:w-16 sm:h-16 rounded flex items-center justify-center transition ${
                  isLoading ? 'bg-green-500' : 'bg-[#F2D857] hover:bg-[#F2D857]/90'
                }`}
                data-testid="track-play-button-alt"
                style={{ touchAction: 'manipulation' }}
              >
                {isLoading ? (
                  <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 text-white animate-spin" />
                ) : (
                  <Play className="w-6 h-6 sm:w-8 sm:h-8 text-black" />
                )}
              </button>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white truncate">{track.title}</h3>
            <p className="text-sm sm:text-base text-[#888888] truncate">{track.enhanced_artist || track.artist}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {track.platform && (
                <span className="text-xs sm:text-sm text-[#F2D857]">{getPlatformIcon(track.platform)}</span>
              )}
              {track.duration && (
                <span className="text-xs sm:text-sm text-[#888888]">• {track.duration}</span>
              )}
            </div>
          </div>
        </div>
        
        {isReviewer && showActions && (
          <div className="flex gap-2 w-full sm:w-auto justify-end sm:justify-start">
            {/* Spotlight Toggle Button - hide when spotlightOnly mode */}
            {!spotlightOnly && (
              <button
                onClick={onSpotlight}
                className={`p-2 rounded transition ${
                  trackIsSpotlighted 
                    ? 'bg-[#F2D857] text-black hover:bg-[#F2D857]/90' 
                    : 'bg-[#333] text-[#F2D857] hover:bg-[#444]'
                }`}
                title={trackIsSpotlighted ? "Remove from Spotlight" : "Add to Spotlight"}
              >
                <Star className={`w-4 h-4 sm:w-5 sm:h-5 ${trackIsSpotlighted ? 'fill-black' : ''}`} />
              </button>
            )}
            
            {/* Finish Button - hide when spotlightOnly mode */}
            {showFinishButton && !spotlightOnly && (
              <button
                onClick={onFinish}
                className="p-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
                title="Mark as Finished"
              >
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
            
            {/* Remove Button - always show (in spotlightOnly mode, this removes from spotlight) */}
            <button
              onClick={onRemove}
              className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
              title={spotlightOnly ? "Remove from Spotlight" : "Remove Track"}
            >
              <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackCard;
